import React, { createContext, useState, useContext, useCallback } from 'react';
import { API } from 'aws-amplify';
import { API_BASE_PATH } from '../components/CommonUtilities/UtilityFunctions';

const StudentsContext = createContext();

export const useStudents = () => {
    const context = useContext(StudentsContext);
    if (!context) {
        throw new Error('useStudents must be used within a StudentsProvider');
    }
    return context;
};

export const StudentsProvider = ({ children }) => {
    const [students, setStudents] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [lastFetched, setLastFetched] = useState(null);
    const [error, setError] = useState(null);

    const loadStudentsApi = () => {
        return API.get(API_BASE_PATH, "/students?details=TESORERIA");
    };

    const loadStudents = useCallback(async (forceRefresh = false) => {
        // If we have data and it's less than 5 minutes old, don't reload unless forced
        const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
        
        setIsLoading(true);
        setError(null);
        try {
            // Check if we should fetch new data
            const shouldFetch = forceRefresh || 
                               students.length === 0 || 
                               !lastFetched || 
                               lastFetched <= fiveMinutesAgo;
            
            if (!shouldFetch) {
                setIsLoading(false);
                return;
            }
            
            const studentsData = await loadStudentsApi();
            setStudents(studentsData);
            setLastFetched(Date.now());
        } catch (e) {
            setError("No se pudo actualizar la lista de estudiantes. Por favor intente de nuevo.");
            console.error("Error loading students:", e);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const updateStudent = useCallback((studentId, updatedData) => {
        setStudents(prevStudents => 
            prevStudents.map(student => 
                student.id === studentId 
                    ? { ...student, ...updatedData }
                    : student
            )
        );
    }, []);

    const clearStudents = useCallback(() => {
        setStudents([]);
        setLastFetched(null);
        setError(null);
    }, []);

    const value = {
        students,
        isLoading,
        error,
        lastFetched,
        loadStudents,
        updateStudent,
        clearStudents,
        forceRefresh: () => loadStudents(true)
    };

    return (
        <StudentsContext.Provider value={value}>
            {children}
        </StudentsContext.Provider>
    );
};